/**
 * Platform detection utilities for handling iOS IAP vs Stripe payments
 * Uses Despia SDK as the primary detection method for native iOS purchases
 */

import { isDespiaNative } from './despia';

export type Platform = 'ios' | 'android' | 'web';

/**
 * Detects the current platform the app is running on
 * Priority: Despia native detection first, then fallbacks
 */
export const getPlatform = (): Platform => {
  const isDespia = isDespiaNative();
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : '';
  const isIOSUserAgent = ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod');
  const isAndroidUserAgent = ua.includes('android');
  
  console.log('[Platform] Detection:', {
    isDespiaNative: isDespia,
    isIOSUserAgent,
    isAndroidUserAgent,
    userAgent: ua.substring(0, 150),
  });
  
  // Priority 1: Despia native detection (user agent contains "despia")
  // This is the PRIMARY method for iOS native app detection
  if (isDespia) {
    if (isIOSUserAgent) {
      console.log('[Platform] Detected: ios (Despia + iOS UA)');
      return 'ios';
    }
    if (isAndroidUserAgent) {
      console.log('[Platform] Detected: android (Despia + Android UA)');
      return 'android';
    }
    // Default to iOS for Despia (most common case)
    console.log('[Platform] Detected: ios (Despia fallback)');
    return 'ios';
  }
  
  // Non-Despia environments fall through to web
  console.log('[Platform] Detected: web');
  return 'web';
};

/**
 * Check if we're running on iOS native (requires Apple IAP via Despia)
 */
export const isIOSNative = (): boolean => {
  return getPlatform() === 'ios';
};

/**
 * Check if we're running on Android native
 */
export const isAndroidNative = (): boolean => {
  return getPlatform() === 'android';
};

/**
 * Check if we're running in a native mobile app
 */
export const isNativeApp = (): boolean => {
  const platform = getPlatform();
  return platform === 'ios' || platform === 'android';
};

/**
 * Determines which payment method to use based on platform
 * iOS (Despia native) uses RevenueCat via Despia protocol
 * Android and Web use Stripe
 */
export const getPaymentProvider = (): 'revenuecat' | 'stripe' => {
  // Only use RevenueCat when in Despia native environment AND iOS
  const useRevenueCat = isDespiaNative() && isIOSNative();
  const provider = useRevenueCat ? 'revenuecat' : 'stripe';
  console.log('[Platform] Payment provider:', provider, { isDespiaNative: isDespiaNative(), isIOSNative: isIOSNative() });
  return provider;
};
